import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, FileText, AlertTriangle, CheckCircle, Loader2, FileSearch } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../config';

export default function DocumentReviewPage() {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [reviewType, setReviewType] = useState('general');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const reviewTypes = [
    { value: 'general', label: 'Ogólna analiza', desc: 'Pełna weryfikacja rachunkowa i prawna' },
    { value: 'invoice', label: 'Faktura VAT', desc: 'Sprawdzenie wymaganych elementów faktury' },
    { value: 'contract', label: 'Umowa', desc: 'Analiza klauzul prawnych i podatkowych' },
    { value: 'tax', label: 'Dokument podatkowy', desc: 'Weryfikacja zgodności z przepisami' },
  ];

  const handleSubmit = async () => {
    if (!file) { toast.error('Wybierz plik do analizy'); return; }
    setLoading(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('review_type', reviewType);
      const resp = await axios.post(`${API_URL}/review-document`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      setResult(resp.data);
      toast.success('Analiza dokumentu zakończona');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Błąd analizy dokumentu');
    } finally {
      setLoading(false);
    }
  };

  const formatReview = (text) => {
    if (!text) return '';
    return text
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/## (.*?)(\n|$)/g, '<h3 class="text-lg font-bold text-primary-900 dark:text-primary-300 mt-5 mb-2">$1</h3>')
      .replace(/# (.*?)(\n|$)/g, '<h2 class="text-xl font-bold text-primary-900 dark:text-primary-200 mt-6 mb-3">$1</h2>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/^- (.*?)$/gm, '<li class="ml-4 mb-1">$1</li>')
      .replace(/\n/g, '<br/>');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" onClick={() => navigate('/dashboard')} className="gap-2">
            <ArrowLeft className="w-4 h-4" /> Powrót
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-primary-900 dark:text-white">Sprawdź dokument</h1>
            <p className="text-sm text-muted-foreground">AI weryfikuje Twój dokument pod kątem rachunkowym i prawnym</p>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border p-6 mb-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2 dark:text-slate-200">Plik dokumentu</label>
              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:border-primary-400 transition-colors dark:border-slate-600"
                data-testid="document-upload-area"
              >
                <input ref={fileRef} type="file" className="hidden" accept=".pdf,.txt,.doc,.docx,.csv,.md,.jpg,.jpeg,.png,.webp"
                  onChange={e => setFile(e.target.files[0])} />
                {file ? (
                  <div className="flex items-center justify-center gap-2 text-primary-700 dark:text-primary-400">
                    <FileText className="w-6 h-6" />
                    <span className="font-medium">{file.name}</span>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                    <p className="text-sm text-slate-500 dark:text-slate-400">Kliknij aby wybrać plik</p>
                    <p className="text-xs text-slate-400 mt-1">PDF, TXT, DOC, CSV (max 10MB)</p>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 dark:text-slate-200">Typ analizy</label>
              <div className="space-y-2">
                {reviewTypes.map(rt => (
                  <button key={rt.value} data-testid={`review-type-${rt.value}`}
                    onClick={() => setReviewType(rt.value)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      reviewType === rt.value
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-400'
                        : 'border-slate-200 dark:border-slate-600 hover:border-slate-300'
                    }`}>
                    <p className="font-medium text-sm dark:text-slate-200">{rt.label}</p>
                    <p className="text-xs text-muted-foreground">{rt.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
          <Button onClick={handleSubmit} disabled={!file || loading}
            className="w-full mt-6 h-12 bg-primary-600 hover:bg-primary-700 text-white gap-2" data-testid="analyze-document-btn">
            {loading ? <><Loader2 className="w-5 h-5 animate-spin" /> Analizuję dokument...</> :
              <><FileSearch className="w-5 h-5" /> Sprawdź dokument</>}
          </Button>
        </div>

        {result && (
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border p-6" data-testid="document-review-result">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-6 h-6 text-green-500" />
              <div>
                <h2 className="font-bold text-lg dark:text-white">Wynik analizy: {result.filename}</h2>
                <p className="text-xs text-muted-foreground">Typ: {reviewTypes.find(r=>r.value===result.review_type)?.label} | Rozmiar: {Math.round(result.document_length/1024)} KB</p>
              </div>
            </div>
            <div className="prose prose-sm dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: formatReview(result.review) }} />
          </div>
        )}
      </div>
    </div>
  );
}
